import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowLeft, Gift, Users, Copy, Share2, Check, Sparkles, Clock, Trophy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const Referral = ({ user }) => {
  const navigate = useNavigate();
  const [referralData, setReferralData] = useState(null);
  const [pendingReferrals, setPendingReferrals] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyCode, setApplyCode] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    fetchData();
  }, [user.id]);

  const fetchData = async () => {
    try {
      const [codeRes, pendingRes, historyRes] = await Promise.all([
        axios.get(`${API}/referral/code/${user.id}`),
        axios.get(`${API}/referral/pending/${user.id}`),
        axios.get(`${API}/referral/history/${user.id}`)
      ]);
      
      setReferralData(codeRes.data);
      setPendingReferrals(pendingRes.data.pending_referrals || []);
      setHistory(historyRes.data.referral_history || []);
    } catch (error) {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (referralData?.referral_code) {
      navigator.clipboard.writeText(referralData.referral_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareCode = async () => {
    if (navigator.share && referralData?.share_message) {
      try {
        await navigator.share({
          title: 'Join SharePay!',
          text: referralData.share_message,
          url: window.location.origin
        });
      } catch (error) {
        // User cancelled or error
        copyCode();
      }
    } else {
      copyCode();
    }
  };

  const applyReferralCode = async () => {
    if (!applyCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }

    setApplying(true);
    try {
      const response = await axios.post(
        `${API}/referral/apply?user_id=${user.id}&referral_code=${applyCode.trim()}`
      );
      toast.success(response.data.message);
      setShowApplyModal(false);
      setApplyCode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply referral code');
    } finally {
      setApplying(false);
    }
  };

  const claimBonuses = async () => {
    try {
      const response = await axios.post(`${API}/referral/claim-bonus/${user.id}`);
      if (response.data.claimed_count > 0) {
        toast.success(`Claimed ${response.data.total_xp_earned} XP and ${response.data.total_points_earned} points!`);
        fetchData();
      } else {
        toast.info('No bonuses to claim yet. Your referrals need to make their first transaction.');
      }
    } catch (error) {
      toast.error('Failed to claim bonuses');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
      {/* Header */}
      <div className="bg-card/80 backdrop-blur-xl border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-20 h-20 mx-auto mb-4 rounded-full gradient-primary flex items-center justify-center">
            <Gift className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-2" data-testid="referral-title">Invite & Earn</h1>
          <p className="text-muted-foreground">
            Share SharePay with friends and earn rewards together!
          </p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card rounded-2xl p-6 border border-border text-center"
          >
            <Users className="w-8 h-8 mx-auto mb-2 text-violet-600" />
            <p className="text-3xl font-bold">{referralData?.referral_count || 0}</p>
            <p className="text-sm text-muted-foreground">Successful Referrals</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card rounded-2xl p-6 border border-border text-center"
          >
            <Sparkles className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
            <p className="text-3xl font-bold">{referralData?.total_xp_earned || 0}</p>
            <p className="text-sm text-muted-foreground">XP Earned</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card rounded-2xl p-6 border border-border text-center"
          >
            <Trophy className="w-8 h-8 mx-auto mb-2 text-orange-500" />
            <p className="text-3xl font-bold">{referralData?.total_points_earned || 0}</p>
            <p className="text-sm text-muted-foreground">Points Earned</p>
          </motion.div>
        </div>

        {/* Referral Code Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-3xl p-6 mb-8 text-white"
        >
          <h2 className="text-lg font-semibold mb-4">Your Referral Code</h2>
          <div className="flex items-center gap-3 bg-white/20 rounded-xl p-4">
            <span className="text-2xl font-bold tracking-wider flex-1" data-testid="referral-code">
              {referralData?.referral_code || 'Loading...'}
            </span>
            <Button
              variant="secondary"
              size="icon"
              onClick={copyCode}
              className="bg-white/20 hover:bg-white/30 text-white"
              data-testid="copy-code-btn"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </Button>
          </div>
          
          <div className="flex gap-3 mt-4">
            <Button
              onClick={shareCode}
              className="flex-1 bg-white text-violet-600 hover:bg-gray-100"
              data-testid="share-code-btn"
            >
              <Share2 className="w-4 h-4 mr-2" /> Share with Friends
            </Button>
          </div>

          <div className="mt-4 pt-4 border-t border-white/20">
            <p className="text-sm opacity-90">
              🎁 You get <strong>100 XP + 50 Points</strong> when your friend makes their first transaction
            </p>
            <p className="text-sm opacity-90 mt-1">
              🎁 Your friend gets <strong>50 XP + 25 Points</strong> just for signing up!
            </p>
          </div>
        </motion.div>

        {/* Apply Referral Code */}
        {!user.referred_by && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-card rounded-2xl p-6 border border-border mb-8"
          >
            <h3 className="font-semibold mb-2">Have a referral code?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Apply a friend's code to earn bonus XP and points!
            </p>
            <Button 
              onClick={() => setShowApplyModal(true)}
              variant="outline"
              className="w-full"
              data-testid="apply-code-btn"
            >
              <Gift className="w-4 h-4 mr-2" /> Apply Referral Code
            </Button>
          </motion.div>
        )}

        {/* Pending Referrals */}
        {pendingReferrals.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-card rounded-2xl p-6 border border-border mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-500" />
                Pending Bonuses
              </h3>
              <Button 
                onClick={claimBonuses}
                size="sm"
                className="gradient-primary"
                data-testid="claim-bonus-btn"
              >
                Claim All
              </Button>
            </div>
            <div className="space-y-3">
              {pendingReferrals.map((ref, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                  <div>
                    <p className="font-medium">{ref.referred_name}</p>
                    <p className="text-xs text-muted-foreground">{ref.status}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-violet-600">+{ref.potential_xp} XP</p>
                    <p className="text-xs text-muted-foreground">+{ref.potential_points} pts</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Referral History */}
        {history.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-card rounded-2xl p-6 border border-border"
          >
            <h3 className="font-semibold mb-4">Referral History</h3>
            <div className="space-y-3">
              {history.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                  <div>
                    <p className="font-medium">{item.user_name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{item.status}</p>
                  </div>
                  <div className="text-right">
                    {item.status === 'claimed' ? (
                      <>
                        <p className="text-sm font-semibold text-green-600">+{item.xp_earned} XP</p>
                        <p className="text-xs text-muted-foreground">+{item.points_earned} pts</p>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">Pending</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Apply Code Modal */}
      <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Apply Referral Code</DialogTitle>
            <DialogDescription>
              Enter your friend's referral code to earn bonus rewards!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input
              value={applyCode}
              onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
              placeholder="Enter referral code"
              className="text-center text-lg tracking-widest"
              data-testid="referral-code-input"
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowApplyModal(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 gradient-primary"
                onClick={applyReferralCode}
                disabled={applying || !applyCode.trim()}
                data-testid="submit-referral-btn"
              >
                {applying ? 'Applying...' : 'Apply Code'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Referral;
